<div class="page aform">
  <header>
    <a class="back-link" uiSref="article">
      <span class="material-icons" aria-hidden="true">arrow_back</span>
      Articles
    </a>
    <h1 class="page-h__title">{{ isNew ? 'New article' : 'Edit article' }}</h1>
  </header>

  @if (isNew) {
    <div class="alert alert--info">
      <span class="material-icons" aria-hidden="true">info_outline</span>
      <p class="alert__text">
        When creating an article, please make sure that you are properly <a href="https://www.utest.com/articles/2728" target="_blank" rel="noopener">citing<span class="sr-only"> (opens in a new tab)</span></a>
        your sources if you are drawing from previously authored material. Additionally, please note that articles are meant
        for longer content, and material such as quotes or questions should be left to the forums or posted as a status.
      </p>
    </div>
  }

  <div class="aform__layout">
    <form class="card aform__card" (submit)="$event.preventDefault()" novalidate>
      @if ((submitted() && errors.length) || serverError()) {
        <div class="errsum" tabindex="-1" role="alert">
          @if (errors.length) {
            <b>Fix {{ errors.length === 1 ? '1 thing' : errors.length + ' things' }} to save</b>
            <ul>
              @for (e of errors; track e.field) {
                <li><button type="button" class="errsum__link" (click)="focusField(e.field)">{{ e.message }}</button></li>
              }
            </ul>
          } @else {
            <b>Couldn't save the article.</b> {{ serverError() }}
          }
        </div>
      }

      <p class="field__hint">Fields marked * are required.</p>

      <div class="field">
        <label class="field__label" for="articleTitle">Title <span class="field__req" aria-hidden="true">*</span></label>
        <input
          id="articleTitle"
          class="field__input"
          type="text"
          required
          [value]="title()"
          [class.is-invalid]="error('title')"
          [attr.aria-invalid]="!!error('title')"
          [attr.aria-describedby]="error('title') ? 'articleTitleError' : null"
          (input)="setTitle($any($event.target).value)">
        @if (error('title'); as message) {
          <p class="field__error" id="articleTitleError">{{ message }}</p>
        }
      </div>

      <div class="field">
        <span class="field__label">Article text <span class="field__req" aria-hidden="true">*</span></span>
        <ut-editor
          [id]="fieldIds.content"
          [value]="content()"
          config="full"
          textAreaLabel="Article text"
          [mentionsRelativeToParent]="true"
          [textRequired]="true"
          [ariaInvalid]="!!error('content')"
          [ariaDescribedBy]="error('content') ? 'articleContentError' : null"
          (valueChange)="setContent($event)"></ut-editor>
        @if (error('content'); as message) {
          <p class="field__error" id="articleContentError">{{ message }}</p>
        }
      </div>

      <div class="field">
        <label class="field__label" [for]="fieldIds.slug">URL <span class="field__req" aria-hidden="true">*</span></label>
        <ut-slug-input
          [inputId]="fieldIds.slug"
          emptyText="Add a title and the URL is created from it."
          [urlPre]="urlPre()"
          [toSlug]="title()"
          [theSlug]="slug()"
          [hasError]="!!error('slug')"
          errorId="articleSlugError"
          (slugChange)="setSlug($event)"></ut-slug-input>
        @if (error('slug'); as message) {
          <p class="field__error" id="articleSlugError">{{ message }}</p>
        }
      </div>

      <div class="field">
        <label class="field__label" for="articleTags">Tags</label>
        <ut-tag-selector
          inputId="articleTags"
          [availableTags]="availableTags()"
          [selectedTags]="selectedTags()"
          [maxTags]="2"
          placeholder="Add up to 2 tags"
          (selectedTagsChange)="setTags($event)"></ut-tag-selector>
        <p class="field__hint">Up to 2 tags. Tags help testers find your article in search.</p>
      </div>
    </form>

    <aside class="card aform__publish" aria-labelledby="articlePublishing">
      <div class="aform__publish-head">
        <h2 class="aform__publish-title" id="articlePublishing">Publishing</h2>
        <span class="badge" [class.badge--success]="isPublished">{{ statusLabel }}</span>
      </div>

      @if (!isPublished) {
        <div class="aform__autosave">
          <span class="aform__dot" [class]="'aform__dot aform__dot--' + (autosaveOn() ? autosave() : 'off')" aria-hidden="true"></span>
          <span role="status">
            @if (!autosaveOn()) {
              Autosave is off
            } @else {
              @switch (autosave()) {
                @case ('saving') { Saving… }
                @case ('saved') { Saved }
                @case ('error') { {{ autosaveError() }} }
                @default { Autosave is on }
              }
            }
          </span>
          <button type="button" class="aform__toggle" (click)="toggleAutosave()">
            {{ autosaveOn() ? 'Turn off autosave' : 'Turn on autosave' }}
          </button>
        </div>
      }

      <div class="aform__buttons">
        @if (isPublished) {
          <button type="button" class="ut-btn" [disabled]="saving()" (click)="action('save')">Save &amp; publish</button>
        } @else {
          @if (isAdmin) {
            <button type="button" class="ut-btn" [disabled]="saving()" (click)="action('publish')">Publish</button>
          } @else {
            <button type="button" class="ut-btn" [disabled]="saving()" (click)="action('review')">Submit for review</button>
          }
          <button type="button" class="ut-btn ut-btn--secondary" [disabled]="saving()" (click)="action('save')">Save draft</button>
        }
        @if (canDelete) {
          <button type="button" class="ut-btn ut-btn--secondary aform__delete" (click)="confirmDelete()">Delete</button>
        }
      </div>
      <p class="field__hint">
        @if (isAdmin) {
          As a moderator you can publish straight away.
        } @else {
          Moderators review community articles before they're published.
        }
      </p>
    </aside>
  </div>
</div>
